import type { ClientChallenge } from "@coachapp/shared";
import { ChallengeFrequency } from "@coachapp/shared";
import { Archive, Coins, Pause, Play, Plus, Target } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiErrorMessage } from "../lib/api-error";
import {
  coachApi,
  useArchiveChallengeMutation,
  useCreateChallengeMutation,
  useGetChallengesQuery,
  useGetClientCoinsQuery,
  useToggleCoinsMutation,
  useUpdateChallengeMutation,
} from "../store/coachApi";
import { useAppDispatch } from "../store/hooks";
import { bgMeta } from "../store/requestMeta";
import DatePicker from "./DatePicker";
import ErrorAlert from "./ErrorAlert";
import RedeemCoinsModal from "./RedeemCoinsModal";

interface ClientChallengesPanelProps {
  clientId: string;
}

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "badge-success";
    case "PAUSED":
      return "badge bg-amber-500/10 text-amber-700";
    case "ARCHIVED":
      return "badge bg-slate-100 text-slate-600";
    default:
      return "badge bg-slate-100 text-slate-600";
  }
};

const frequencyBadgeClass = (frequency: string) => {
  switch (frequency) {
    case "DAILY":
      return "badge bg-primary-soft text-primary";
    case "WEEKLY":
      return "badge bg-violet-500/10 text-violet-700";
    case "ONCE":
      return "badge bg-slate-500/10 text-slate-700";
    default:
      return "badge bg-slate-100 text-slate-600";
  }
};

const formatTransactionAmount = (tx: { amount: number; type: string }) => {
  const abs = Math.abs(tx.amount);
  if (tx.type === "REDEEM" || tx.amount < 0) {
    return `−${abs}`;
  }
  return `+${abs}`;
};

const transactionAmountClass = (type: string) => {
  if (type === "REDEEM") {
    return "text-red-600 font-semibold";
  }
  return "text-emerald-600 font-semibold";
};

const ClientChallengesPanel = ({ clientId }: ClientChallengesPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    data: allChallenges = [],
    isLoading: challengesLoading,
    error: challengesError,
    refetch: refetchChallenges,
  } = useGetChallengesQuery({ clientId });
  const {
    data: coinsData,
    isLoading: coinsLoading,
    error: coinsError,
    refetch: refetchCoins,
  } = useGetClientCoinsQuery({ clientId });
  const [createChallenge, { isLoading: saving }] = useCreateChallengeMutation();
  const [updateChallenge] = useUpdateChallengeMutation();
  const [archiveChallenge] = useArchiveChallengeMutation();
  const [toggleCoinsMutation, { isLoading: togglingCoins }] = useToggleCoinsMutation();

  const [showForm, setShowForm] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [showRedeemModal, setShowRedeemModal] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [coachMessage, setCoachMessage] = useState("");
  const [coinReward, setCoinReward] = useState("");
  const [frequency, setFrequency] = useState<string>(ChallengeFrequency.DAILY);
  const [weeklyTarget, setWeeklyTarget] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [error, setError] = useState<string | null>(null);

  const challenges = useMemo(
    () => allChallenges.filter((c) => c.status !== "ARCHIVED"),
    [allChallenges],
  );
  const coinBalance = coinsData?.coinBalance ?? 0;
  const coinsEnabled = coinsData?.coinsEnabled ?? false;
  const history = coinsData?.history ?? [];
  const loading = challengesLoading || coinsLoading;

  const fetchError =
    challengesError || coinsError
      ? getApiErrorMessage(
          challengesError || coinsError,
          "Не удалось загрузить данные",
        )
      : null;

  const refetchBackground = useCallback(() => {
    void dispatch(
      coachApi.endpoints.getChallenges.initiate(
        { clientId, ...bgMeta(true) },
        { forceRefetch: true },
      ),
    );
    void dispatch(
      coachApi.endpoints.getClientCoins.initiate(
        { clientId, ...bgMeta(true) },
        { forceRefetch: true },
      ),
    );
  }, [clientId, dispatch]);

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCoachMessage("");
    setCoinReward("");
    setFrequency(ChallengeFrequency.DAILY);
    setWeeklyTarget("");
    setDueAt("");
    setShowForm(false);
  };

  const handleToggleCoins = async () => {
    const next = !coinsEnabled;
    setError(null);
    try {
      await toggleCoinsMutation({ clientId, coinsEnabled: next }).unwrap();
      refetchBackground();
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err && typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось изменить настройку коинов"),
      );
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const reward = Number(coinReward);
    if (!title.trim() || !reward || reward <= 0) {
      return;
    }

    if (
      frequency === ChallengeFrequency.WEEKLY &&
      (!weeklyTarget || Number(weeklyTarget) <= 0)
    ) {
      return;
    }

    setError(null);
    try {
      await createChallenge({
        clientId,
        title: title.trim(),
        description: description.trim() || undefined,
        coachMessage: coachMessage.trim() || undefined,
        coinReward: reward,
        frequency,
        weeklyTarget:
          frequency === ChallengeFrequency.WEEKLY
            ? Number(weeklyTarget)
            : undefined,
        dueAt:
          frequency === ChallengeFrequency.ONCE && dueAt
            ? new Date(dueAt).toISOString()
            : undefined,
      }).unwrap();
      resetForm();
      refetchBackground();
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err && typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось создать задание"),
      );
    }
  };

  const handlePause = async (challenge: ClientChallenge) => {
    setActionId(challenge.id);
    setError(null);
    try {
      await updateChallenge({
        id: challenge.id,
        clientId,
        data: { status: "PAUSED" },
      }).unwrap();
      refetchBackground();
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err && typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось приостановить задание"),
      );
    } finally {
      setActionId(null);
    }
  };

  const handleResume = async (challenge: ClientChallenge) => {
    setActionId(challenge.id);
    setError(null);
    try {
      await updateChallenge({
        id: challenge.id,
        clientId,
        data: { status: "ACTIVE" },
      }).unwrap();
      refetchBackground();
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err && typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось возобновить задание"),
      );
    } finally {
      setActionId(null);
    }
  };

  const handleArchive = async (challenge: ClientChallenge) => {
    if (!confirm(t("Вы уверены, что хотите архивировать это задание?"))) {
      return;
    }

    setActionId(challenge.id);
    setError(null);
    try {
      await archiveChallenge({ id: challenge.id, clientId }).unwrap();
      refetchBackground();
    } catch (err) {
      setError(
        err && typeof err === "object" && "message" in err && typeof err.message === "string"
          ? err.message
          : getApiErrorMessage(err, "Не удалось архивировать задание"),
      );
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="card p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-slate-900">
          {t("Задания и коины")}
        </h2>
        {coinsEnabled && !showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="btn-primary text-sm py-2 w-full sm:w-auto shrink-0"
          >
            <Plus size={16} />
            <span>{t("Задание")}</span>
          </button>
        )}
      </div>

      {(error || fetchError) && (
        <ErrorAlert
          message={error || fetchError!}
          onRetry={() => {
            void refetchChallenges();
            void refetchCoins();
          }}
          onDismiss={() => setError(null)}
          className="mb-4"
        />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 mb-5 rounded-2xl border border-slate-100 bg-slate-50">
        <div className="flex items-center gap-3 min-w-0">
          <Coins size={20} className="text-amber-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {t("Система коинов")}
            </p>
            <p className="text-xs text-slate-500">
              {t("Включить начисление коинов за задания")}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={coinsEnabled}
          disabled={togglingCoins}
          onClick={handleToggleCoins}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors disabled:opacity-50 ${
            coinsEnabled ? "bg-primary" : "bg-slate-300"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              coinsEnabled ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {coinsEnabled && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 p-4 rounded-2xl border border-amber-100 bg-amber-50/50">
          <div>
            <p className="micro-label text-amber-600 mb-1">
              {t("Баланс коинов")}
            </p>
            <p className="text-2xl font-bold text-slate-900">{coinBalance}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowRedeemModal(true)}
            disabled={coinBalance <= 0}
            className="btn-secondary text-sm py-2 w-full sm:w-auto shrink-0 disabled:opacity-50"
          >
            <Coins size={16} />
            <span>{t("Списать коины")}</span>
          </button>
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="mb-5 p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3"
        >
          <div>
            <label className="section-label mb-1 block">
              {t("Название задания")}
            </label>
            <input
              type="text"
              className="input-field"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("Название задания")}
              required
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="section-label mb-1 block">
                {t("Частота")}
              </label>
              <select
                className="input-field"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
              >
                <option value={ChallengeFrequency.DAILY}>
                  {t("challengeFrequency.DAILY")}
                </option>
                <option value={ChallengeFrequency.WEEKLY}>
                  {t("challengeFrequency.WEEKLY")}
                </option>
                <option value={ChallengeFrequency.ONCE}>
                  {t("challengeFrequency.ONCE")}
                </option>
              </select>
            </div>
            <div>
              <label className="section-label mb-1 block">
                {t("Награда, коины")}
              </label>
              <input
                type="number"
                min={1}
                className="input-field"
                value={coinReward}
                onChange={(e) => setCoinReward(e.target.value)}
                required
              />
            </div>
          </div>
          {frequency === ChallengeFrequency.WEEKLY && (
            <div>
              <label className="section-label mb-1 block">
                {t("Цель за неделю")}
              </label>
              <input
                type="number"
                min={1}
                className="input-field"
                value={weeklyTarget}
                onChange={(e) => setWeeklyTarget(e.target.value)}
                required
              />
            </div>
          )}
          {frequency === ChallengeFrequency.ONCE && (
            <div>
              <label className="section-label mb-1 block">
                {t("Срок выполнения")}
              </label>
              <DatePicker
                value={dueAt}
                onChange={setDueAt}
                clearable
              />
            </div>
          )}
          <div>
            <label className="section-label mb-1 block">
              {t("Сообщение тренера")}
            </label>
            <input
              type="text"
              className="input-field"
              value={coachMessage}
              onChange={(e) => setCoachMessage(e.target.value)}
              placeholder={t("Сообщение тренера")}
            />
          </div>
          <div>
            <label className="section-label mb-1 block">
              {t("Описание")}
            </label>
            <textarea
              className="input-field min-h-[60px] resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm py-2"
            >
              {saving ? t("Сохранение...") : t("Сохранить")}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="btn-secondary text-sm py-2"
            >
              {t("Отмена")}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="spinner h-8 w-8" />
        </div>
      ) : !coinsEnabled ? (
        <div className="empty-state py-8">
          <Coins size={40} className="mb-3 opacity-20" />
          <p className="text-sm text-slate-500">
            {t("Система коинов выключена")}
          </p>
        </div>
      ) : challenges.length === 0 ? (
        <div className="empty-state py-8">
          <Target size={40} className="mb-3 opacity-20" />
          <p className="text-sm text-slate-500">{t("Нет заданий")}</p>
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          {challenges.map((challenge) => (
            <div
              key={challenge.id}
              className="p-4 rounded-2xl border border-slate-100 bg-white hover:border-slate-200 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-bold text-slate-900 truncate">
                      {challenge.title}
                    </h3>
                    <span className={frequencyBadgeClass(challenge.frequency)}>
                      {t(
                        `challengeFrequency.${challenge.frequency}`,
                        challenge.frequency,
                      )}
                    </span>
                    <span className={statusBadgeClass(challenge.status)}>
                      {t(
                        `challengeStatus.${challenge.status}`,
                        challenge.status,
                      )}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                    <span>
                      {t("Награда")}:{" "}
                      <span className="font-semibold text-amber-600">
                        {challenge.coinReward} {t("коинов")}
                      </span>
                    </span>
                    {challenge.frequency === "WEEKLY" &&
                      challenge.weeklyTarget && (
                        <span>
                          {t("Цель")}:{" "}
                          <span className="font-semibold text-slate-900">
                            {challenge.weeklyTarget}/{t("нед")}
                          </span>
                        </span>
                      )}
                    {challenge.dueAt && (
                      <span>
                        {t("Срок")}:{" "}
                        <span className="font-semibold text-slate-900">
                          {new Date(challenge.dueAt).toLocaleDateString(
                            undefined,
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </span>
                      </span>
                    )}
                  </div>
                  {challenge.coachMessage && (
                    <p className="text-xs text-slate-500 mt-1 italic">
                      {challenge.coachMessage}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                  {challenge.status === "ACTIVE" ? (
                    <button
                      type="button"
                      onClick={() => handlePause(challenge)}
                      disabled={actionId === challenge.id}
                      className="btn-secondary text-sm py-2 disabled:opacity-50"
                      title={t("Приостановить")}
                    >
                      {actionId === challenge.id ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      ) : (
                        <Pause size={16} />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleResume(challenge)}
                      disabled={actionId === challenge.id}
                      className="btn-secondary text-sm py-2 disabled:opacity-50"
                      title={t("Возобновить")}
                    >
                      {actionId === challenge.id ? (
                        <div className="w-4 h-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      ) : (
                        <Play size={16} />
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleArchive(challenge)}
                    disabled={actionId === challenge.id}
                    className="btn-secondary text-sm py-2 disabled:opacity-50"
                    title={t("Архивировать")}
                  >
                    <Archive size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {coinsEnabled && history.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-900 mb-3">
            {t("История транзакций")}
          </h3>
          <ul className="sm:hidden space-y-2">
            {history.map((tx) => (
              <li
                key={tx.id}
                className="p-3 rounded-2xl border border-slate-100 bg-slate-50/50 space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">
                    {new Date(tx.createdAt).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span className={transactionAmountClass(tx.type)}>
                    {formatTransactionAmount(tx)}
                  </span>
                </div>
                <p className="text-sm text-slate-700">
                  {t(`coinTransactionType.${tx.type}`, tx.type)}
                </p>
                {tx.note && (
                  <p className="text-xs text-slate-500 break-words">{tx.note}</p>
                )}
              </li>
            ))}
          </ul>
          <div className="hidden sm:block max-w-full rounded-2xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-2 sm:px-4 py-2 section-label">
                    {t("Дата")}
                  </th>
                  <th className="px-2 sm:px-4 py-2 section-label">
                    {t("Тип")}
                  </th>
                  <th className="px-2 sm:px-4 py-2 section-label">
                    {t("Сумма")}
                  </th>
                  <th className="px-2 sm:px-4 py-2 section-label hidden sm:table-cell">
                    {t("Примечание")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((tx) => (
                  <tr key={tx.id} className="border-t border-slate-100">
                    <td className="px-2 sm:px-4 py-2 text-slate-600 whitespace-nowrap">
                      {new Date(tx.createdAt).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-slate-600">
                      {t(`coinTransactionType.${tx.type}`, tx.type)}
                    </td>
                    <td
                      className={`px-2 sm:px-4 py-2 ${transactionAmountClass(tx.type)}`}
                    >
                      {formatTransactionAmount(tx)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 text-slate-500 max-w-[200px] truncate hidden sm:table-cell">
                      {tx.note || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showRedeemModal && (
        <RedeemCoinsModal
          clientId={clientId}
          maxBalance={coinBalance}
          onClose={() => setShowRedeemModal(false)}
          onSuccess={() => {
            refetchBackground();
          }}
        />
      )}
    </div>
  );
};

export default ClientChallengesPanel;
